import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import {
  Package, Users, ShieldCheck, Building2, Globe, Sparkles, ArrowRight,
  AlertTriangle, Newspaper, Utensils, Bell, Activity, TrendingUp, BookOpen,
} from "lucide-react";

/**
 * FoodPulse CMS dashboard — landing view.
 * Renders live count tiles + jump links into every managed surface.
 * All tiles degrade gracefully if the endpoint isn't ready yet.
 */

const TILES = [
  { key: "products_pending", label: "Products awaiting approval", to: "/admin/cms/products/pending", icon: ShieldCheck, tint: "amber" },
  { key: "products_total",   label: "Products in catalog",         to: "/admin/cms/products",         icon: Package,     tint: "emerald" },
  { key: "users_total",      label: "Registered users",            to: "/admin/cms/users",            icon: Users,       tint: "sky" },
  { key: "businesses_total", label: "Business customers",          to: "/admin/cms/businesses",       icon: Building2,   tint: "violet" },
  { key: "websites_total",   label: "Customer websites",           to: "/admin/cms/websites",         icon: Globe,       tint: "rose" },
  { key: "deals_active",     label: "Active deals",                to: "/admin/cms/deals",            icon: Sparkles,    tint: "orange" },
];

const QUICK_LINKS = [
  { to: "/admin/cms/pages",      label: "Edit landing pages",      icon: Newspaper },
  { to: "/admin/cms/guides",     label: "Cornerstone guides",      icon: BookOpen },
  { to: "/admin/cms/contributors", label: "Expert contributors",   icon: Users },
  { to: "/admin/cms/food-news",  label: "Food News moderation",    icon: Newspaper },
  { to: "/admin/cms/recalls",    label: "Recalls & alerts",        icon: AlertTriangle },
  { to: "/admin/cms/alerts",     label: "Broadcast alerts",        icon: Bell },
  { to: "/admin/cms/recipes",    label: "Recipes catalog",         icon: Utensils },
  { to: "/admin/cms/correspondence", label: "User correspondence", icon: Activity },
];

const TINT_STYLES = {
  amber:   "bg-amber-50 text-amber-700",
  emerald: "bg-emerald-50 text-emerald-700",
  sky:     "bg-sky-50 text-sky-700",
  violet:  "bg-violet-50 text-violet-700",
  rose:    "bg-rose-50 text-rose-700",
  orange:  "bg-orange-50 text-orange-700",
};

export default function CmsHome() {
  const [counts, setCounts] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ov, an] = await Promise.all([
          http.get("/admin/cms/overview"),
          http.get("/admin/cms/analytics", { params: { days: 7 } }).catch(() => null),
        ]);
        if (!cancelled) {
          setCounts(ov.data || {});
          setAnalytics(an?.data || null);
        }
      } catch {
        // The overview endpoint may not exist in older backends — ignore.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="max-w-6xl" data-testid="cms-home">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">FoodPulse Content Center</h1>
        <p className="mt-1 text-sm text-slate-500">
          Everything you need to keep the site running — products, pages, users, businesses, websites, alerts and correspondence — in one place.
        </p>
      </header>

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-3" data-testid="cms-tiles">
        {TILES.map((t) => {
          const Icon = t.icon;
          const val = loading ? "…" : (counts[t.key] ?? "—");
          return (
            <Link
              key={t.key}
              to={t.to}
              data-testid={`cms-tile-${t.key}`}
              className="group relative flex items-start justify-between rounded-xl border border-slate-200 bg-white p-4 transition hover:border-emerald-300 hover:shadow-sm"
            >
              <div>
                <div className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${TINT_STYLES[t.tint]}`}>
                  <Icon className="h-4.5 w-4.5" />
                </div>
                <div className="mt-3 text-2xl font-semibold tabular-nums text-slate-900">{val}</div>
                <div className="mt-1 text-xs text-slate-500">{t.label}</div>
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 transition group-hover:translate-x-1 group-hover:text-slate-500" />
            </Link>
          );
        })}
      </section>

      <section className="mt-8">
        <h2 className="mb-2 text-base font-semibold text-slate-900">Quick links</h2>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {QUICK_LINKS.map((q) => {
            const Icon = q.icon;
            return (
              <Link
                key={q.to}
                to={q.to}
                data-testid={`cms-quick-${q.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 hover:border-emerald-300 hover:bg-emerald-50"
              >
                <span className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-slate-400" /> {q.label}
                </span>
                <ArrowRight className="h-4 w-4 text-slate-300" />
              </Link>
            );
          })}
        </div>
      </section>

      {/* Analytics rail — rolling {days}-day activity across managed surfaces. */}
      <section className="mt-8 rounded-xl border border-slate-200 bg-white p-5" data-testid="cms-analytics-rail">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Last 7 days</h2>
            <p className="text-xs text-slate-500">Snapshot of everything the CMS is managing.</p>
          </div>
          <TrendingUp className="h-4 w-4 text-emerald-500" />
        </div>
        {!analytics ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {[
                ["new_users", "New signups"],
                ["product_edits", "Products edited"],
                ["recipes_updated", "Recipes updated"],
                ["deals_created", "Deals created"],
                ["alerts_created", "Alerts sent"],
                ["correspondence_new", "New messages"],
              ].map(([k, label]) => (
                <div key={k} className="rounded-lg bg-slate-50 p-3" data-testid={`cms-analytics-${k}`}>
                  <div className="text-xl font-semibold tabular-nums text-slate-900">{analytics[k] ?? 0}</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">{label}</div>
                </div>
              ))}
            </div>
            {analytics.top_pages?.length > 0 && (
              <div className="mt-4">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Top pages</div>
                <ul className="space-y-0.5 text-xs text-slate-700">
                  {analytics.top_pages.slice(0, 5).map((p) => (
                    <li key={p.path} className="flex items-center justify-between">
                      <span className="truncate">{p.path}</span>
                      <span className="tabular-nums text-slate-500">{p.hits}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </section>

      <section className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-5 w-5 text-emerald-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-emerald-900">
              The FoodPulse Agent is coming next.
            </p>
            <p className="mt-1 text-xs text-emerald-800/80">
              A per-page AI assistant that answers questions about the current page context plus the whole foodpulse.net catalog will attach to every CMS view once the LLM budget resets.
            </p>
          </div>
          <div className="ml-2 flex shrink-0 flex-col gap-1">
            <Link
              to="/install"
              data-testid="cms-home-install-app"
              className="rounded-lg border border-emerald-300 bg-white px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
            >
              Install the app →
            </Link>
            <Link
              to="/admin/cms/homepage-and-push"
              data-testid="cms-home-homepage-push"
              className="rounded-lg border border-emerald-300 bg-white px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
            >
              Homepage &amp; Push →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
